import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Carousel for `sections/mmx-product-family.liquid` and the MMX card strip in
 * `sections/product-recommendations.liquid`.
 *
 * When the section's "Add to cart instead of linking" option is on, each card
 * renders a `[data-mmx-family-atc]` button instead of a link. Those are not
 * product forms — the element posts a single line to `/cart/add.js` the way the
 * other mmx sections do, and announces it with `CartLinesUpdateEvent` so the
 * cart drawer opens optimistically and settles on the promise.
 */
class MmxProductFamily extends HTMLElement {
  /** @type {boolean} */
  #submitting = false;

  /** @type {number | undefined} */
  #addedTimeout;

  /**
   * Listeners are bound per connection: a section re-render in the theme editor
   * moves the element rather than replacing it, so `connectedCallback` runs again
   * and un-scoped listeners would stack (each Add click counting twice, and so on).
   * @type {AbortController}
   */
  #listeners = new AbortController();

  connectedCallback() {
    this.#listeners = new AbortController();
    this.#setupAddToCart();
    this.#setupCarousel();
  }

  disconnectedCallback() {
    this.#listeners.abort();
    if (this.#addedTimeout) clearTimeout(this.#addedTimeout);
  }

  /* ------------------------------- Carousel ------------------------------- */

  #setupCarousel() {
    this.track = this.querySelector('.mmx-family__grid');
    if (!this.track) return;

    this.items = Array.from(this.track.querySelectorAll('.mmx-family__item'));
    this.pagination = this.querySelector('.mmx-family__pagination');
    if (this.items.length <= 1) {
      this.querySelectorAll('.mmx-family__controls').forEach((el) => {
        el.style.display = 'none';
      });
      return;
    }

    const { signal } = this.#listeners;

    // The recommendations section morphs this element in after fetching, and the
    // theme editor re-connects it, so start from an empty strip rather than
    // appending a second set of dots.
    if (this.pagination) this.pagination.textContent = '';
    this.#syncStops();

    this.querySelectorAll('.mmx-family__arrow--prev').forEach((btn) => {
      btn.addEventListener('click', () => this.step(-1), { signal });
    });
    this.querySelectorAll('.mmx-family__arrow--next').forEach((btn) => {
      btn.addEventListener('click', () => this.step(1), { signal });
    });

    this.track.addEventListener(
      'scroll',
      () => {
        window.clearTimeout(this.#scrollTimer);
        this.#scrollTimer = window.setTimeout(() => this.syncActive(), 80);
      },
      { signal }
    );

    // Card and track widths change with the viewport, and with them how many stops exist.
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => this.#syncStops());
    this.#resizeObserver.observe(this.track);
    signal.addEventListener('abort', () => this.#resizeObserver?.disconnect());
  }

  #scrollTimer = 0;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /**
   * The scroll positions the track can actually rest at, one per dot.
   *
   * On mobile every card can be centred, so there is a stop per card. The desktop
   * slider shows several cards at once and aligns them to the start edge, so the
   * last few all clamp to the end of the track and share its stop: six cards at
   * 1440px give four stops, the last one showing cards four to six.
   * @type {number[]}
   */
  #stops = [];

  /**
   * Where an item sits inside the track's scroll area. Measured against the track
   * rather than `offsetLeft`, which is relative to whichever ancestor is positioned
   * and only lines up when the track starts at the section edge (mobile).
   * @param {HTMLElement} item
   */
  #itemLeft(item) {
    return item.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
  }

  /** The desktop slider snaps cards to the start edge; the mobile carousel centres them. */
  get #alignsStart() {
    return getComputedStyle(this.items[0]).scrollSnapAlign.includes('start');
  }

  /** @param {HTMLElement} item */
  #targetScroll(item) {
    const left = this.#itemLeft(item);
    if (this.#alignsStart) return left - parseFloat(getComputedStyle(this.track).paddingLeft);
    return left - (this.track.clientWidth - item.offsetWidth) / 2;
  }

  /** Recomputes the stops and rebuilds the dots when their number changes. */
  #syncStops() {
    const maxScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
    const stops = [];
    this.items.forEach((item) => {
      const left = Math.min(Math.max(this.#targetScroll(item), 0), maxScroll);
      // Sub-pixel card widths leave neighbouring clamped targets a pixel or two apart.
      if (stops.length === 0 || left - stops[stops.length - 1] > 2) stops.push(left);
    });
    this.#stops = stops;

    const idle = stops.length <= 1;
    this.querySelectorAll('.mmx-family__controls').forEach((el) => el.toggleAttribute('data-idle', idle));

    if (this.pagination && this.pagination.children.length !== stops.length) {
      this.pagination.textContent = '';
      const { signal } = this.#listeners;
      stops.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'mmx-family__dot';
        dot.setAttribute('aria-label', `Go to slide ${i + 1} of ${stops.length}`);
        dot.addEventListener('click', () => this.scrollToStop(i), { signal });
        this.pagination.appendChild(dot);
      });
    }

    this.syncActive();
  }

  /** The stop nearest the current scroll position. */
  get currentStop() {
    let best = 0;
    let bestDistance = Infinity;
    this.#stops.forEach((left, i) => {
      const distance = Math.abs(left - this.track.scrollLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });
    return best;
  }

  step(direction) {
    this.scrollToStop(Math.min(Math.max(this.currentStop + direction, 0), this.#stops.length - 1));
  }

  scrollToStop(index) {
    const left = this.#stops[index];
    if (left === undefined) return;
    this.track.scrollTo({ left, behavior: 'smooth' });
  }

  syncActive() {
    const active = this.currentStop;
    this.querySelectorAll('.mmx-family__dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === active);
    });
  }

  /* ------------------------------ Add to cart ----------------------------- */

  #setupAddToCart() {
    const buttons = Array.from(this.querySelectorAll('[data-mmx-family-atc]'));
    if (buttons.length === 0) return;

    const { signal } = this.#listeners;

    buttons.forEach((button) => {
      button.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          const id = Number(button.dataset.variantId);
          if (!id) return;
          this.#addToCart(id, button);
        },
        { signal }
      );
    });
  }

  /**
   * @param {number} variantId
   * @param {HTMLElement} trigger
   */
  async #addToCart(variantId, trigger) {
    if (this.#submitting) return;

    this.#submitting = true;
    trigger.setAttribute('aria-busy', 'true');
    if (trigger instanceof HTMLButtonElement) trigger.disabled = true;

    const items = [{ id: variantId, quantity: 1 }];
    const deferred = CartLinesUpdateEvent.createPromise();

    // Dispatched before the request so the cart drawer can open optimistically;
    // it stays in its loading state until the promise below resolves.
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: this.#cartSectionIds() }),
      }).then((res) => res.json());

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );

        const cart = await this.#refreshCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'mmx-product-family', itemCount: 1 },
        });
        return;
      }

      const cart = await this.#refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'mmx-product-family',
          itemCount: 1,
          sections: response.sections,
        },
      });

      this.#showAdded(trigger);
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#submitting = false;
      trigger.removeAttribute('aria-busy');
      if (trigger instanceof HTMLButtonElement) trigger.disabled = false;
    }
  }

  #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean)
      .join(',');
  }

  #fetchCartJson() {
    return fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());
  }

  async #refreshCart() {
    const cartItems = document.querySelector('cart-items-component');

    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }

    return this.#fetchCartJson();
  }

  /** @param {HTMLElement} trigger */
  #showAdded(trigger) {
    trigger.classList.add('is-added');
    if (this.#addedTimeout) clearTimeout(this.#addedTimeout);
    this.#addedTimeout = setTimeout(() => trigger.classList.remove('is-added'), 2000);
  }
}

if (!customElements.get('mmx-product-family')) {
  customElements.define('mmx-product-family', MmxProductFamily);
}
